// src/pages/MaterialsManagementPage.js
import React, { useState, useEffect } from 'react';
import { materialsAPI } from '../services/api';

const MaterialsManagementPage = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentMaterial, setCurrentMaterial] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    unit: '',
    description: '',
    price_per_unit: '0.00'
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    setLoading(true);
    try {
      const response = await materialsAPI.getAll();
      setMaterials(response.data.materials);
      setError('');
    } catch (err) {
      setError('Fehler beim Laden der Materialien');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (material) => {
    setCurrentMaterial(material);
    setFormData({
      name: material.name,
      unit: material.unit,
      description: material.description || '',
      price_per_unit: parseFloat(material.price_per_unit).toFixed(2)
    });
    setShowEditModal(true);
  };

  const handleDeleteClick = (material) => {
    setCurrentMaterial(material);
    setShowDeleteConfirm(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'price_per_unit') {
      // Sicherstellen, dass immer ein gültiger Dezimalwert entsteht
      const formattedValue = value === '' 
        ? '0.00' 
        : (parseFloat(value.replace(',', '.')) || 0).toFixed(2);
      
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await materialsAPI.update(currentMaterial.id, {
        ...formData,
        price_per_unit: parseFloat(formData.price_per_unit)
      });
      
      setShowEditModal(false);
      setSuccessMessage('Material erfolgreich aktualisiert');
      setTimeout(() => setSuccessMessage(''), 3000);
      await fetchMaterials();
    } catch (err) {
      setError('Fehler beim Aktualisieren des Materials');
      console.error(err);
    }
  };

  const handleDelete = async () => {
    try {
      await materialsAPI.delete(currentMaterial.id);
      setShowDeleteConfirm(false);
      setSuccessMessage('Material erfolgreich gelöscht');
      setTimeout(() => setSuccessMessage(''), 3000);
      await fetchMaterials();
    } catch (err) {
      setError('Fehler beim Löschen des Materials');
      console.error(err);
    }
  };

  if (loading) {
    return <div className="text-center py-10">Lade Materialien...</div>;
  }

  return (
    <div className="container mx-auto p-2 sm:p-4 md:p-6 max-w-full">
      <h1 className="text-2xl font-semibold mb-6">Materialverwaltung</h1>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6">
          <p className="text-green-700">{successMessage}</p>
        </div>
      )}

      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-96">
            <h2 className="text-xl font-semibold mb-4">Material bearbeiten</h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-1">Einheit *</label>
                  <input
                    type="text"
                    name="unit"
                    value={formData.unit}
                    onChange={handleFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-1">Beschreibung</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    rows="3"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-1">Preis pro Einheit (CHF) *</label>
                  <input
                    type="text"
                    name="price_per_unit"
                    value={formData.price_per_unit}
                    onChange={handleFormChange}
                    pattern="^\d+(\.\d{1,2})?$"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 text-white rounded"
                  >
                    Speichern
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-96">
            <h2 className="text-xl font-semibold mb-4">Material löschen</h2>
            <p className="mb-4">
              Sind Sie sicher, dass Sie das Material "{currentMaterial.name}" löschen möchten?
              Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wider">Einheit</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wider">Beschreibung</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wider">Preis pro Einheit</th>
                <th className="px-3 sm:px-6 py-3 text-right text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {materials.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                    Keine Materialien gefunden
                  </td>
                </tr>
              ) : (
                materials.map((material) => (
                  <tr key={material.id}>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      {material.name}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      {material.unit}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      {material.description || '-'}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      CHF {parseFloat(material.price_per_unit).toFixed(2)}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex flex-nowrap justify-end space-x-2">
                        <button
                          onClick={() => handleEditClick(material)}
                          className="text-primary-600 hover:text-primary-900 bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded"
                        >
                          Bearbeiten
                        </button>
                        <button
                          onClick={() => handleDeleteClick(material)}
                          className="text-red-600 hover:text-red-900 bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded"
                        >
                          Löschen
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MaterialsManagementPage;